/**
 * Recorder Zustand slice (FEATURE BUILD G1 §2) — binding registry + the
 * reactive `recSampleTick` heartbeat for the graphing suite.
 *
 * Registered into the root store (`src/store/index.ts`) with the standard
 * chatSlice pattern: `...createRecorderSlice(set, get, api)` + `& RecorderSlice`
 * on `AppState`.
 *
 * SELECTOR HYGIENE (CLAUDE.md hard rule — no .filter/.map/object literals in
 * selectors): everything a component needs to select is either a PRIMITIVE
 * (`recSampleTick`, `recPaused`) or a PRECOMPUTED stable reference
 * (`recBindingKeys`, rebuilt only inside actions). Sample data itself lives in
 * `recorderRuntime` ring buffers OUTSIDE the store; charts re-read buffers in a
 * `useMemo` keyed on `recSampleTick`.
 *
 * Bindings are REF-COUNTED: several graphing nodes may bind the same variable
 * (a gauge and a trend on one pump outlet); the series survives until the last
 * binder unbinds. Binding state is runtime-only — each graphing node re-binds
 * from its persisted `data.bindings` on mount, so canvas load restores the
 * recording set without persisting buffers.
 */
import type { StateCreator } from 'zustand';

import {
  clearAllSeries,
  clearSeries,
  ingestSeriesData,
  type SeriesPoint,
} from './recorderBuffers';
import { refKey, type VariableRef } from './variableRefTypes';

export interface RecorderSliceState {
  /** Live bindings by ref key (`source:nodeId:field`). */
  recBindings: Record<string, VariableRef>;
  /** Ref-count per key (multiple graph nodes may share one binding). */
  recBindCounts: Record<string, number>;
  /** Precomputed stable key list (never compute this in a selector). */
  recBindingKeys: readonly string[];
  /** Monotonic heartbeat — bumped whenever buffers changed. Charts select THIS. */
  recSampleTick: number;
  /** Global recorder pause (per-chart pause is chart-local display state). */
  recPaused: boolean;
}

export interface RecorderSliceActions {
  /** Register interest in a variable (idempotent per caller; ref-counted). */
  recBind: (ref: VariableRef) => void;
  /** Release one interest in a key; the series is dropped at refcount 0. */
  recUnbind: (key: string) => void;
  /** Clear ONE series' samples (binding kept), or ALL series when omitted. */
  recClearSeries: (key?: string) => void;
  /** Reset-on-sim-reset: drop all samples, keep bindings. */
  recResetAll: () => void;
  recSetPaused: (paused: boolean) => void;
  /** Replace a series with an external trajectory (EO transient ingest). */
  recIngestSeries: (key: string, pts: readonly SeriesPoint[]) => void;
}

export type RecorderSlice = RecorderSliceState & RecorderSliceActions;

const initialState: RecorderSliceState = {
  recBindings: {},
  recBindCounts: {},
  recBindingKeys: [],
  recSampleTick: 0,
  recPaused: false,
};

export const createRecorderSlice: StateCreator<RecorderSlice, [], [], RecorderSlice> = (
  set,
) => ({
  ...initialState,

  recBind: (ref) =>
    set((s) => {
      const key = refKey(ref);
      const count = (s.recBindCounts[key] ?? 0) + 1;
      const recBindCounts = { ...s.recBindCounts, [key]: count };
      if (count > 1) return { recBindCounts };
      const recBindings = { ...s.recBindings, [key]: ref };
      return { recBindings, recBindCounts, recBindingKeys: Object.keys(recBindings) };
    }),

  recUnbind: (key) =>
    set((s) => {
      const current = s.recBindCounts[key] ?? 0;
      if (current <= 0) return s;
      if (current > 1) {
        return { recBindCounts: { ...s.recBindCounts, [key]: current - 1 } };
      }
      const recBindings = { ...s.recBindings };
      delete recBindings[key];
      const recBindCounts = { ...s.recBindCounts };
      delete recBindCounts[key];
      clearSeries(key);
      return {
        recBindings,
        recBindCounts,
        recBindingKeys: Object.keys(recBindings),
        recSampleTick: s.recSampleTick + 1,
      };
    }),

  recClearSeries: (key) =>
    set((s) => {
      if (key === undefined) clearAllSeries();
      else clearSeries(key);
      return { recSampleTick: s.recSampleTick + 1 };
    }),

  recResetAll: () =>
    set((s) => {
      clearAllSeries();
      return { recSampleTick: s.recSampleTick + 1 };
    }),

  recSetPaused: (paused) => set({ recPaused: paused }),

  recIngestSeries: (key, pts) =>
    set((s) => {
      ingestSeriesData(key, pts);
      return { recSampleTick: s.recSampleTick + 1 };
    }),
});
